import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { apiEndpoints } from "../../core/constants/api-strings";
import {
  BaseHttpService,
  HttpResult,
} from "../../core/services/base-http.service";
import { BaseResponse } from "../../shared/models/base-response.model";
import {
  AssignChildTicketRequest,
  AssignTicketRequest,
  AssignTicketResponse,
  GetImageUploadLinkDataModel,
  InitiateUpgradePaymentResModel,
  RazorpayUpgradeRequest,
  TicketModel,
  TicketSuggestionResponse,
  UpgradeTicketMediaPayload,
  UpgradeTicketMediaResponse,
  UploadData,
  UserEventStatusResp,
  VerifyUpgradePaymentRequest,
} from "./tickets.model";

@Injectable({
  providedIn: "root",
})
export class TicketsApiService extends BaseHttpService {
  /**
   * @constructor
   * Initializes the service by calling the parent `BaseHttpService` constructor.
   */
  constructor() {
    super();
  }

  // Base URL for farmer-related APIs
  private readonly farmerBaseUrl = environment.farmer_base_url;

  // Logged-in farmer's ID
  private farmerId = this.storage.getLoggedUserId();

  // Event code, defaults to 'KISAN25' if not set
  private eventcode = this.storage.getEventCode() || "KISAN25";

  /**
   * @method assignTicket
   * @description Assigns a ticket to a farmer for a specific event.
   * @param {string} ticketCode - The ticket code to assign.
   * @param {AssignTicketRequest} payload - Body containing mobile, fname, lname, state, city, image_rl.
   * @returns {Observable<HttpResult<BaseResponse<AssignTicketResponse>>>} Observable containing the assignment response.
   */
  assignTicketApi(
    ticketCode: string,
    payload: AssignTicketRequest | AssignChildTicketRequest
  ): Observable<HttpResult<BaseResponse<AssignTicketResponse>>> {
    // Builds: ~/farmers/<id>/events/<eventcode>/tickets/{ticketcode}/assign

    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.tickets +
      ticketCode +
      apiEndpoints.farmerRegister.assign
      }`;

    return this.post<BaseResponse<AssignTicketResponse>>(
      serviceName,
      payload,
      {},
      true
    );
  }

  /**
   * Retrieves tickets for the current farmer and event, optionally filtered by summary ID and assignment status.
   *
   * @param summaryId - (Optional) The summary ID to filter tickets by.
   * @param assigned - (Optional) Assignment status filter: 1 for assigned tickets, 0 for unassigned tickets, or undefined for all tickets.
   * @returns Observable emitting an HTTP result containing a base response with an array of `TicketModel` objects.
   */
  getTicketsApi(
    summaryId?: string | null,
    assigned?: number
  ): Observable<HttpResult<BaseResponse<TicketModel[]>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.tickets
      }`;

    let params: any = {};
    if (summaryId) {
      params.summary_id = summaryId;
    }

    // 'assigned' can be undefined or null since it is an optional parameter;
    // only add it to params if it is a valid number.
    if (typeof assigned === "number") {
      params.assigned = assigned;
    }

    return this.get<BaseResponse<TicketModel[]>>(serviceName, params, true);
  }

  // Fetches ticket suggestions for the current farmer/event context.
  // An Observable that emits the full HTTP response with the typed body.
  getSuggestions(): Observable<
    HttpResult<BaseResponse<TicketSuggestionResponse>>
  > {
    const url = `${this.farmerBaseUrl}${this.farmerId}${apiEndpoints.farmerRegister.events}${this.eventcode}${apiEndpoints.farmerRegister.suggestions}`;
    return this.get<BaseResponse<TicketSuggestionResponse>>(url, {
      observe: "response",
    }, true);
  }

  /**
   * Get a signed URL for uploading an exhibitor logo
   */
  getUploadImageInfoApi(
    payload: GetImageUploadLinkDataModel,
    ticketCode: string
  ): Observable<HttpResult<BaseResponse<UploadData>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.tickets +
      ticketCode +
      apiEndpoints.farmerRegister.ticket_image_upload_link
      }`;

    return this.post<BaseResponse<UploadData>>(serviceName, payload);
  }

  /**
   * Upload logo to S3 using pre-signed URL and required fields
   */
  uploadLogoToS3Api(
    mediaUrl: string,
    fields: Record<string, string>,
    file: File
  ) {
    const formData = new FormData();

    // Append required S3 fields
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Append the file
    formData.append("file", file);

    return this.s3BucketPost<HttpResult<any>>(mediaUrl, formData);
  }

  /**
   * Fetches detailed information about a specific ticket using its ticket code.
   * @param ticketCode - The unique code of the ticket to retrieve details for.
   */
  getTicketDetail(
    ticketCode: string
  ): Observable<HttpResult<BaseResponse<TicketModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.tickets +
      ticketCode
      }`;
    return this.get<BaseResponse<TicketModel>>(serviceName, {}, true);
  }

  // Initiates an upgrade payment with the backend.
  // Sends the upgrade option and ticket code to the server, which creates a Razorpay order.
  initiateUpgradePayment(
    ticketCode: string,
    payload: RazorpayUpgradeRequest
  ): Observable<HttpResult<BaseResponse<InitiateUpgradePaymentResModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.tickets +
      ticketCode +
      apiEndpoints.farmerRegister.initiateUpgradePayment
      }`;
    return this.post<BaseResponse<InitiateUpgradePaymentResModel>>(
      serviceName,
      payload,
      {},
      true
    );
  }

  // Verifies an upgrade payment with the backend.
  // Sends the Razorpay confirmation payload to the server, which validates the
  // payment signature and updates the ticket.
  verifyUpgradePayment(
    ticketCode: string,
    payload: VerifyUpgradePaymentRequest
  ): Observable<HttpResult<BaseResponse<any>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.tickets +
      ticketCode +
      apiEndpoints.farmerRegister.verifyUpgradePayment
      }`;
    return this.post<BaseResponse<any>>(serviceName, payload, {}, true);
  }


  /**
   * Sends failed upgrade payment details to backend for logging and reconciliation.
   *
   * Used when:
   * - Razorpay payment fails
   * - User dismisses the payment popup after order creation
   *
   * @param ticketCode  Unique ticket code for which upgrade payment failed
   * @param payload     Failure details (order_id, payment_id, error_code, description, etc.)
   * @returns           Observable wrapping backend API response
   */
  failedUpgradePayment(
    ticketCode: string,
    payload: any
  ): Observable<HttpResult<BaseResponse<any>>> {

    // Build API endpoint for failed upgrade payment
    const serviceName =
      this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.tickets +
      ticketCode +
      apiEndpoints.farmerRegister.failedUpgradePayment;

    // POST failure payload to backend (auth + loader enabled)
    return this.post<BaseResponse<any>>(serviceName, payload, {}, true);
  }

  // Fetches the user's event status for the current farmer and event.
  updateImageforUpgradeTicket(ticketCode: string, payload: UpgradeTicketMediaPayload
  ): Observable<HttpResult<BaseResponse<UpgradeTicketMediaResponse>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.tickets +
      ticketCode +
      apiEndpoints.farmerRegister.updateUploadTicketMedia
      }`;

    return this.put<BaseResponse<UpgradeTicketMediaResponse>>(serviceName, payload, {}, true);
  }
}